import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  generateShotList,
  downloadShotListPdf,
  updateShotList,
  saveShotLengths,
  regenerateShot,
  continueShotList,
} from "../services/scriptService";
import {
  getVideoCredits,
  getVideoClips,
  generateVideoClip,
  getVideoClipUrl,
  VideoClip,
  VideoCredits,
} from "../services/mediaService";
import { ScriptResponse } from "../types";
import TimedVoiceoverPanel from "./TimedVoiceoverPanel";

interface Shot {
  number: string;
  scene: string; // the scene heading this shot belongs to (empty for older shot lists)
  prompt: string;
  note: string;
}

// Turns the saved shot list text into separate shots
const parseShots = (shotList: string): Shot[] => {
  return shotList
    .split(/\n(?=SHOT \d+\s*\n)/)
    .map((block) => block.trim())
    .filter((block) => block.length > 0)
    .map((block) => {
      const lines = block.split("\n");
      const number = lines[0].replace("SHOT", "").trim();
      // SCENES: read the "Scene:" label, and keep it out of the prompt
      const sceneLine = lines
        .slice(1)
        .find((l) => l.trim().startsWith("Scene:"));
      const scene = sceneLine
        ? sceneLine.replace(/^\s*Scene:\s*/, "").trim()
        : "";
      const rest = lines
        .slice(1)
        .filter((l) => !l.trim().startsWith("Scene:"))
        .join("\n");
      const noteMatch = rest.match(/\n?((Upload|Narration):[\s\S]*)$/);
      const note = noteMatch ? noteMatch[1].trim() : "";
      const prompt = (noteMatch ? rest.slice(0, noteMatch.index) : rest)
        .replace(/^Prompt:\s*/, "")
        .trim();
      return { number, scene, prompt, note };
    });
};

// EDIT SHOT: turns the shots back into the saved text format
const buildShotList = (shots: Shot[]): string =>
  shots
    .map(
      (s) =>
        `SHOT ${s.number}\n${s.scene ? `Scene: ${s.scene}\n` : ""}Prompt: ${s.prompt}${s.note ? `\n${s.note}` : ""}`,
    )
    .join("\n\n");

// Small button style, matching "Copy prompt"
const SMALL_BTN =
  "px-3 py-1 rounded-md text-xs transition whitespace-nowrap disabled:opacity-50";

const ShotListPanel = ({ script }: { script: ScriptResponse }) => {
  const [shotList, setShotList] = useState(script.shotList || "");
  const [loading, setLoading] = useState(false);
  // CONTINUE: the shot list stopped part-way and can be continued
  const [incomplete, setIncomplete] = useState(!!script.shotListIncomplete);
  // CONTINUE: its own busy state, so the Regenerate button doesn't look clicked
  const [continuing, setContinuing] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [error, setError] = useState("");
  const [copiedShot, setCopiedShot] = useState<string | null>(null);
  const [copiedAll, setCopiedAll] = useState(false);

  // EDIT SHOT: which shot is being edited, and its draft text
  const [editingNumber, setEditingNumber] = useState<string | null>(null);
  const [editPrompt, setEditPrompt] = useState("");
  const [editNote, setEditNote] = useState("");
  const [savingShot, setSavingShot] = useState(false);
  const [savedShot, setSavedShot] = useState<string | null>(null);
  // REDO SHOT: which shot is being redone
  const [redoingShot, setRedoingShot] = useState<string | null>(null);

  // VIDEO: credits, each shot's clip status, and loaded video links
  const [credits, setCredits] = useState<VideoCredits | null>(null);
  const [clips, setClips] = useState<Record<string, VideoClip>>({});
  const [videoUrls, setVideoUrls] = useState<Record<string, string>>({});
  const [startingVideo, setStartingVideo] = useState<string | null>(null);
  const pollRef = useRef<number | null>(null);

  // TIMED VOICEOVER: each shot's clip length (8 seconds unless set to 10)
  const [shotLengths, setShotLengths] = useState<Record<string, number>>(() => {
    try {
      return script.shotLengths ? JSON.parse(script.shotLengths) : {};
    } catch {
      return {};
    }
  });

  // TIMED VOICEOVER: the 8s / 10s buttons only show when the switch in the Timed voiceover section is on
  const [showLengths, setShowLengths] = useState<boolean>(() => {
    try {
      return localStorage.getItem("tubescript-show-clip-lengths") === "on";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    const onToggle = () => {
      try {
        setShowLengths(
          localStorage.getItem("tubescript-show-clip-lengths") === "on",
        );
      } catch {}
    };
    window.addEventListener("clip-lengths-toggle", onToggle);
    return () => window.removeEventListener("clip-lengths-toggle", onToggle);
  }, []);

  const { user } = useAuth();
  const navigate = useNavigate();

  const canDownloadPdf = user?.plan === "PRO" || user?.plan === "AGENCY";
  const shots = shotList ? parseShots(shotList) : [];

  // VIDEO: load credits and clips once the shot list exists
  const loadVideoInfo = async () => {
    try {
      const [c, list] = await Promise.all([
        getVideoCredits(),
        getVideoClips(script.id),
      ]);
      setCredits(c);
      const map: Record<string, VideoClip> = {};
      list.forEach((clip) => (map[String(clip.shotNumber)] = clip));
      setClips(map);
    } catch {
      // Video info is optional; the shot list still works without it
    }
  };

  useEffect(() => {
    if (shots.length > 0) loadVideoInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [script.id, shotList.length > 0]);

  // VIDEO: while any clip is still being made, check again every 20 seconds
  useEffect(() => {
    const anyPending = Object.values(clips).some((c) => c.status === "PENDING");
    if (anyPending && pollRef.current === null) {
      pollRef.current = window.setInterval(loadVideoInfo, 20000);
    }
    if (!anyPending && pollRef.current !== null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current !== null) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clips]);

  // Free loaded videos from memory when leaving the page
  useEffect(() => {
    return () => {
      Object.values(videoUrls).forEach((u) => window.URL.revokeObjectURL(u));
    };
  }, [videoUrls]);

  const handleGenerate = async (regenerate: boolean) => {
    setError("");
    setEditingNumber(null);
    setLoading(true);
    try {
      const updated = await generateShotList(script.id, regenerate);
      setShotList(updated.shotList || "");
      setIncomplete(!!updated.shotListIncomplete);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          "Could not create the shot list. If some shots were made, refresh the page to see them and press Continue.",
      );
    } finally {
      setLoading(false);
    }
  };

  // CONTINUE: carry on an unfinished shot list from where it stopped
  const handleContinue = async () => {
    setError("");
    setEditingNumber(null);
    setContinuing(true);
    try {
      const updated = await continueShotList(script.id);
      setShotList(updated.shotList || shotList);
      setIncomplete(!!updated.shotListIncomplete);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          "Could not continue the shot list. Please wait a minute and try again.",
      );
    } finally {
      setContinuing(false);
    }
  };

  const handleCopy = (shot: Shot) => {
    navigator.clipboard.writeText(shot.prompt);
    setCopiedShot(shot.number);
    setTimeout(() => setCopiedShot(null), 2000);
  };

  // COPY ALL: copies every shot, numbered, with its upload line
  const handleCopyAll = () => {
    const text = shots
      .map((s) => `SHOT ${s.number}\n${s.prompt}${s.note ? `\n${s.note}` : ""}`)
      .join("\n\n");
    navigator.clipboard.writeText(text);
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 2000);
  };

  const handlePdf = async () => {
    if (!canDownloadPdf) {
      navigate("/pricing");
      return;
    }
    setError("");
    setPdfLoading(true);
    try {
      await downloadShotListPdf(script.id, script.title);
    } catch (err: any) {
      let message = "Could not download the PDF. Please try again.";
      try {
        const text = await err.response?.data?.text();
        const parsed = JSON.parse(text);
        if (parsed?.message) message = parsed.message;
      } catch {}
      setError(message);
    } finally {
      setPdfLoading(false);
    }
  };

  // EDIT SHOT: open the editor for one shot
  const handleStartEdit = (shot: Shot) => {
    setEditingNumber(shot.number);
    setEditPrompt(shot.prompt);
    setEditNote(shot.note);
    setError("");
  };

  // EDIT SHOT: save just this shot, keeping all the others the same
  const handleSaveShot = async () => {
    if (!editingNumber) return;
    if (!editPrompt.trim()) {
      setError("The shot prompt cannot be empty.");
      return;
    }
    setSavingShot(true);
    setError("");
    try {
      const updatedShots = shots.map((s) =>
        s.number === editingNumber
          ? { ...s, prompt: editPrompt.trim(), note: editNote.trim() }
          : s,
      );
      const updated = await updateShotList(
        script.id,
        buildShotList(updatedShots),
      );
      setShotList(updated.shotList || buildShotList(updatedShots));
      setSavedShot(editingNumber);
      setTimeout(() => setSavedShot(null), 2500);
      setEditingNumber(null);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          "Could not save this shot. Please try again.",
      );
    } finally {
      setSavingShot(false);
    }
  };

  // REDO SHOT: rewrite just this one shot with the AI, keeping all the others the same
  const handleRedoShot = async (shot: Shot) => {
    setError("");
    setRedoingShot(shot.number);
    try {
      const updated = await regenerateShot(script.id, Number(shot.number));
      setShotList(updated.shotList || shotList);
      setSavedShot(shot.number);
      setTimeout(() => setSavedShot(null), 2500);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Could not redo this shot. Please try again.",
      );
    } finally {
      setRedoingShot(null);
    }
  };

  // TIMED VOICEOVER: switch a shot between 8 seconds (Flow) and 10 seconds (Gemini), and save it
  const handleToggleLength = async (shot: Shot) => {
    const current = shotLengths[shot.number] || 8;
    const next = { ...shotLengths, [shot.number]: current === 8 ? 10 : 8 };
    setShotLengths(next);
    try {
      const numbered: Record<number, number> = {};
      Object.entries(next).forEach(([k, v]) => (numbered[Number(k)] = v));
      await saveShotLengths(script.id, numbered);
    } catch {
      setError("Could not save the clip length. Please try again.");
    }
  };

  // VIDEO: start making one shot's clip
  const handleGenerateVideo = async (shot: Shot) => {
    setError("");
    setStartingVideo(shot.number);
    try {
      const clip = await generateVideoClip(script.id, Number(shot.number));
      setClips((prev) => ({ ...prev, [shot.number]: clip }));
      setVideoUrls((prev) => {
        if (prev[shot.number]) window.URL.revokeObjectURL(prev[shot.number]);
        const next = { ...prev };
        delete next[shot.number];
        return next;
      });
      setCredits(await getVideoCredits());
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          "Could not start the video. Please try again.",
      );
    } finally {
      setStartingVideo(null);
    }
  };

  // VIDEO: load a finished clip into the player
  const handleShowVideo = async (shot: Shot) => {
    try {
      const url = await getVideoClipUrl(script.id, Number(shot.number));
      setVideoUrls((prev) => ({ ...prev, [shot.number]: url }));
    } catch {
      setError("Could not load this video. Please try again.");
    }
  };

  const handleDownloadVideo = (shot: Shot) => {
    const url = videoUrls[shot.number];
    if (!url) return;
    const link = document.createElement("a");
    link.href = url;
    link.download = `shot-${shot.number}.mp4`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <div className="bg-gray-900 rounded-xl p-4 border border-gray-700">
      {/* Header: title on the left, small buttons on the right, on the same line */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="min-w-0">
          <h3 className="text-gray-400 text-xs font-semibold uppercase">
            Shot List{shots.length > 0 ? ` · ${shots.length} shots` : ""}
          </h3>
          <p className="text-gray-500 text-xs mt-1">
            Ready-to-paste video prompts for Gemini and Flow, one per shot.
          </p>
        </div>

        {shots.length > 0 && (
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={handleCopyAll}
              className={`${SMALL_BTN} bg-gray-700 hover:bg-gray-600 text-white`}
            >
              {copiedAll ? "Copied!" : "Copy all"}
            </button>
            <button
              onClick={() => handleGenerate(true)}
              disabled={loading || continuing}
              className={`${SMALL_BTN} bg-gray-700 hover:bg-gray-600 text-white`}
            >
              {loading ? "Regenerating..." : "Regenerate"}
            </button>
            <button
              onClick={handlePdf}
              disabled={pdfLoading}
              className={`${SMALL_BTN} ${
                canDownloadPdf
                  ? "bg-purple-600 hover:bg-purple-700 text-white"
                  : "bg-gray-800 border border-yellow-600 text-yellow-400 hover:bg-gray-700"
              }`}
            >
              {pdfLoading
                ? "Preparing..."
                : canDownloadPdf
                  ? "PDF"
                  : "PDF (PRO)"}
            </button>
          </div>
        )}
      </div>

      {/* TIMED VOICEOVER: its own section, shown only once shots exist */}
      {shots.length > 0 && (
        <TimedVoiceoverPanel script={{ ...script, shotList }} />
      )}

      {/* VIDEO: credits bar */}
      {shots.length > 0 && credits && credits.canUseVideo && (
        <div className="flex flex-wrap items-center justify-between gap-2 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 mb-3">
          <p className="text-gray-300 text-xs">
            Video credits:{" "}
            <span className="text-purple-400 font-semibold">
              {credits.credits}
            </span>
            {credits.freeClipsPerMonth > 0 && (
              <>
                {" "}
                · Free clips left this month:{" "}
                <span className="text-green-400 font-semibold">
                  {credits.freeClipsLeft}
                </span>
              </>
            )}
          </p>
          <button
            onClick={() => navigate("/video-credits")}
            className={`${SMALL_BTN} bg-purple-600 hover:bg-purple-700 text-white`}
          >
            Buy credits
          </button>
        </div>
      )}

      {error && (
        <div className="bg-red-900 border border-red-700 text-red-300 px-4 py-3 rounded-lg text-sm mb-3">
          {error}
        </div>
      )}

      {/* CONTINUE: the shot list stopped part-way; pick up where it left off */}
      {incomplete && shots.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 bg-gray-800 border border-yellow-600 rounded-lg px-3 py-2 mb-3">
          <p className="text-yellow-400 text-xs">
            Shot list incomplete: {shots.length} shots so far. Press Continue to
            make the rest.
          </p>
          <button
            onClick={handleContinue}
            disabled={loading || continuing}
            className={`${SMALL_BTN} bg-purple-600 hover:bg-purple-700 text-white`}
          >
            {continuing ? "Continuing..." : "Continue"}
          </button>
        </div>
      )}

      {shots.length === 0 ? (
        <button
          onClick={() => handleGenerate(false)}
          disabled={loading}
          className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-purple-900 disabled:cursor-not-allowed text-white py-3 rounded-xl font-semibold transition"
        >
          {loading
            ? "Creating your shot list... this can take a minute"
            : "Generate Shot List"}
        </button>
      ) : (
        <div className="space-y-3">
          {shots.map((shot, index) => {
            const clip = clips[shot.number];
            const length = shotLengths[shot.number] || 8;
            // SCENES: show a scene heading whenever a new scene starts
            const isNewScene =
              shot.scene &&
              (index === 0 || shots[index - 1].scene !== shot.scene);
            const sceneNumber = shots
              .slice(0, index + 1)
              .filter(
                (s, i) =>
                  s.scene && (i === 0 || shots[i - 1].scene !== s.scene),
              ).length;
            // SCENES: this shot's place within its scene, e.g. "Scene 4, shot 2 of 3"
            let sceneStart = index;
            while (sceneStart > 0 && shots[sceneStart - 1].scene === shot.scene)
              sceneStart--;
            let sceneEnd = index;
            while (
              sceneEnd < shots.length - 1 &&
              shots[sceneEnd + 1].scene === shot.scene
            )
              sceneEnd++;
            const shotInScene = index - sceneStart + 1;
            const shotsInScene = sceneEnd - sceneStart + 1;
            return (
              <div key={shot.number} className="space-y-3">
                {isNewScene && (
                  <div className="pt-2 pl-3 border-l-4 border-purple-500">
                    <p className="text-purple-400 text-sm font-semibold">
                      Scene {sceneNumber} · {shotsInScene}{" "}
                      {shotsInScene === 1 ? "shot" : "shots"}
                    </p>
                    <p className="text-white text-sm font-semibold mt-0.5">
                      {shot.scene}
                    </p>
                  </div>
                )}
                <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="text-purple-400 font-semibold text-sm">
                      Shot {shot.number}
                      {shot.scene &&
                        ` · Scene ${sceneNumber}, shot ${shotInScene} of ${shotsInScene}`}
                      {savedShot === shot.number && (
                        <span className="text-green-400 font-normal ml-2">
                          Saved
                        </span>
                      )}
                    </span>
                    {editingNumber !== shot.number && (
                      <div className="flex gap-2">
                        {/* TIMED VOICEOVER: this shot's clip length (only when switched on) */}
                        {showLengths && (
                          <button
                            onClick={() => handleToggleLength(shot)}
                            title="Clip length: 8 seconds (Flow) or 10 seconds (Gemini). Used by the timed voiceover."
                            className={`${SMALL_BTN} ${
                              length === 10
                                ? "bg-blue-600 hover:bg-blue-700 text-white"
                                : "bg-gray-700 hover:bg-gray-600 text-white"
                            }`}
                          >
                            {length}s
                          </button>
                        )}
                        <button
                          onClick={() => handleStartEdit(shot)}
                          disabled={editingNumber !== null}
                          className={`${SMALL_BTN} bg-gray-700 hover:bg-gray-600 text-white`}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleRedoShot(shot)}
                          disabled={
                            redoingShot !== null || editingNumber !== null
                          }
                          className={`${SMALL_BTN} bg-gray-700 hover:bg-gray-600 text-white`}
                        >
                          {redoingShot === shot.number ? "Redoing..." : "Redo"}
                        </button>
                        <button
                          onClick={() => handleCopy(shot)}
                          className={`${SMALL_BTN} bg-purple-600 hover:bg-purple-700 text-white`}
                        >
                          {copiedShot === shot.number
                            ? "Copied!"
                            : "Copy prompt"}
                        </button>
                        {/* VIDEO: generate this shot (PRO and Agency) */}
                        {credits?.canUseVideo ? (
                          <button
                            onClick={() => handleGenerateVideo(shot)}
                            disabled={
                              startingVideo !== null ||
                              clip?.status === "PENDING"
                            }
                            className={`${SMALL_BTN} bg-green-600 hover:bg-green-700 text-white`}
                          >
                            {startingVideo === shot.number
                              ? "Starting..."
                              : clip?.status === "PENDING"
                                ? "Making video..."
                                : clip?.status === "READY"
                                  ? "Redo video"
                                  : "Video"}
                          </button>
                        ) : (
                          credits && (
                            <button
                              onClick={() => navigate("/pricing")}
                              className={`${SMALL_BTN} bg-gray-800 border border-yellow-600 text-yellow-400 hover:bg-gray-700`}
                            >
                              Video (PRO)
                            </button>
                          )
                        )}
                      </div>
                    )}
                  </div>

                  {editingNumber === shot.number ? (
                    <div className="space-y-2">
                      <label className="block text-gray-400 text-xs">
                        Prompt
                      </label>
                      <textarea
                        value={editPrompt}
                        onChange={(e) => setEditPrompt(e.target.value)}
                        rows={8}
                        className="w-full bg-gray-900 border border-gray-600 text-white p-3 rounded-lg focus:outline-none focus:border-purple-500 transition resize-y text-sm leading-relaxed"
                      />
                      <label className="block text-gray-400 text-xs">
                        Upload / Narration line
                      </label>
                      <textarea
                        value={editNote}
                        onChange={(e) => setEditNote(e.target.value)}
                        rows={2}
                        className="w-full bg-gray-900 border border-gray-600 text-white p-3 rounded-lg focus:outline-none focus:border-purple-500 transition resize-y text-sm"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => setEditingNumber(null)}
                          disabled={savingShot}
                          className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-2 rounded-lg text-sm transition"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveShot}
                          disabled={savingShot}
                          className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-900 text-white py-2 rounded-lg font-semibold text-sm transition"
                        >
                          {savingShot ? "Saving..." : "Save"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-gray-300 text-sm whitespace-pre-line leading-relaxed">
                        {shot.prompt}
                      </p>
                      {shot.note && (
                        <p className="text-gray-400 text-xs font-semibold mt-3">
                          {shot.note}
                        </p>
                      )}
                    </>
                  )}

                  {/* VIDEO: status and player for this shot */}
                  {clip?.status === "PENDING" && (
                    <p className="text-yellow-400 text-xs mt-3">
                      Making this video... it usually takes 1 to 3 minutes. You
                      can keep working; it will appear here.
                    </p>
                  )}
                  {clip?.status === "FAILED" && (
                    <p className="text-red-400 text-xs mt-3">
                      {clip.errorMessage}
                    </p>
                  )}
                  {clip?.status === "READY" && (
                    <div className="mt-3 space-y-2">
                      {videoUrls[shot.number] ? (
                        <>
                          <video
                            controls
                            src={videoUrls[shot.number]}
                            className="w-full rounded-lg border border-gray-600"
                          />
                          <button
                            onClick={() => handleDownloadVideo(shot)}
                            className={`${SMALL_BTN} bg-gray-700 hover:bg-gray-600 text-white`}
                          >
                            Download video
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => handleShowVideo(shot)}
                          className={`${SMALL_BTN} bg-green-600 hover:bg-green-700 text-white`}
                        >
                          Play video
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ShotListPanel;
